import { GOOGLE } from "../../../../utils/constants";
import {
  MULTIPLE_CONVERSION_PREFIX,
  aggregateMultipleConversionsByGroup,
  getConversionActionMap,
  getTranslatedGroupName,
} from "../../../../services/google";
import { METRICS_TO_BROKEN_DOWN_BY_PLATFORM } from "./actionMetricsConfig";
import {
  GENERAL_METRICS_ADDITIONAL,
  GENERAL_METRICS_ORDERED,
  MAP_TRANSLATIONS,
  PLATFORM_METRICS,
} from "./platformMetrics";

export const getGeneralMetricKeys = () => [
  ...GENERAL_METRICS_ORDERED,
  ...GENERAL_METRICS_ADDITIONAL,
];

export const getActionMetricKeys = (platform) => {
  const metricsToBrokenDownByPlatform =
    METRICS_TO_BROKEN_DOWN_BY_PLATFORM[platform];
  if (!metricsToBrokenDownByPlatform) return [];

  const keys = [];
  metricsToBrokenDownByPlatform.forEach(
    ({ costMetricName, countMetricName }) => {
      keys.push(costMetricName, countMetricName);
    }
  );
  return keys;
};

const getActionValue = (actionsArray, actionType) => {
  if (
    !actionsArray ||
    !Array.isArray(actionsArray) ||
    actionsArray.length === 0
  ) {
    return null;
  }

  try {
    const action = actionsArray.find(
      (item) => item?.action_type === actionType
    );

    if (!action || action.value === undefined || action.value === null) {
      return null;
    }

    const parsedValue = parseFloat(action.value);

    if (isNaN(parsedValue) || !isFinite(parsedValue) || parsedValue < 0) {
      return null;
    }

    return parsedValue;
  } catch (error) {
    console.warn(`Error extracting value for ${actionType}:`, error);
    return null;
  }
};

export const computeActionMetrics = (raw = {}, platform) => {
  const computed = {};
  const keys = [];
  const metricsToBrokenDownByPlatform =
    METRICS_TO_BROKEN_DOWN_BY_PLATFORM[platform];

  if (!metricsToBrokenDownByPlatform) {
    return { computed, keys };
  }

  metricsToBrokenDownByPlatform.forEach((config) => {
    const { key, countMetricName, costMetricName } = config;
    const countValue = getActionValue(raw?.actions, key);
    const directCostValue = getActionValue(raw?.costPerActionType, key);

    let costPerAction = null;
    if (directCostValue !== null && directCostValue > 0) {
      costPerAction = directCostValue;
    } else if (raw?.spend && countValue !== null && countValue > 0) {
      costPerAction = raw.spend / countValue;
    }

    const validCostPerAction =
      costPerAction !== null &&
      !isNaN(costPerAction) &&
      isFinite(costPerAction) &&
      costPerAction > 0
        ? costPerAction
        : 0;

    computed[costMetricName] = validCostPerAction;
    keys.push(costMetricName);

    const validCountValue =
      countValue !== null && countValue > 0 ? countValue : 0;
    computed[countMetricName] = validCountValue;
    keys.push(countMetricName);
  });

  return { computed, keys };
};

export const computeMultipleConversions = (
  raw = {},
  platform,
  conversionActions
) => {
  const arr = raw?.multipleConversions;
  if (platform !== GOOGLE || !Array.isArray(arr) || arr.length === 0) {
    return { computed: {}, keys: [] };
  }

  const idToCategoryOrigin = getConversionActionMap(conversionActions);
  const computed = aggregateMultipleConversionsByGroup(arr, idToCategoryOrigin);
  return { computed, keys: Object.keys(computed) };
};

export const getMetricsToShow = ({
  platform,
  multipleConversionsKeys = [],
  actionMetricsKeys = [],
}) => {
  if (!platform || !PLATFORM_METRICS[platform]) {
    return [
      ...getGeneralMetricKeys(),
      ...multipleConversionsKeys,
      ...actionMetricsKeys,
    ];
  }

  const allGeneralMetrics = getGeneralMetricKeys();
  const platformSpecificMetrics = PLATFORM_METRICS[platform].filter(
    (metric) => !allGeneralMetrics.includes(metric)
  );

  return [
    ...GENERAL_METRICS_ORDERED,
    ...GENERAL_METRICS_ADDITIONAL,
    ...platformSpecificMetrics,
    ...multipleConversionsKeys,
    ...actionMetricsKeys,
  ];
};

export const mergeCampaignLevelStatistics = ({
  metricsToShow = [],
  raw = {},
  actionMetricsValues = {},
  multipleConversionsComputed = {},
}) => {
  const defaultMetrics = {};
  metricsToShow.forEach((metric) => {
    defaultMetrics[metric] = 0;
  });

  const normalizedRawStatistics = {};
  Object.keys(raw || {}).forEach((key) => {
    if (key === "multipleConversions" || key === "error") return;
    const value = raw[key];
    if (typeof value === "number") {
      normalizedRawStatistics[key] = value;
    } else if (value === null || value === undefined) {
      return;
    } else {
      normalizedRawStatistics[key] = value;
    }
  });

  return {
    ...defaultMetrics,
    ...normalizedRawStatistics,
    ...actionMetricsValues,
    ...multipleConversionsComputed,
  };
};

export const resolveCampaignLevelStatistics = ({
  raw = {},
  platform,
  conversionActions,
}) => {
  const { computed: actionMetricsValues, keys: actionMetricsKeys } =
    computeActionMetrics(raw, platform);
  const {
    computed: multipleConversionsComputed,
    keys: multipleConversionsKeys,
  } = computeMultipleConversions(raw, platform, conversionActions);
  const metricsToShow = getMetricsToShow({
    platform,
    multipleConversionsKeys,
    actionMetricsKeys,
  });

  return {
    statistics: mergeCampaignLevelStatistics({
      metricsToShow,
      raw,
      actionMetricsValues,
      multipleConversionsComputed,
    }),
    metricsToShow,
    multipleConversionsKeys,
    actionMetricsKeys,
  };
};

export const resolveStatisticsMap = (map, platform, conversionActions) => {
  if (!map || typeof map !== "object" || Array.isArray(map)) return {};

  return Object.fromEntries(
    Object.entries(map).map(([id, raw]) => {
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        return [id, raw];
      }
      return [
        id,
        resolveCampaignLevelStatistics({
          raw,
          platform,
          conversionActions,
        }).statistics,
      ];
    })
  );
};

const visitMultipleConversionNode = (
  raw,
  platform,
  conversionActions,
  keys
) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return;
  computeMultipleConversions(raw, platform, conversionActions).keys.forEach(
    (key) => keys.add(key)
  );
};

export const collectMultipleConversionKeys = (
  platformStatistics = {},
  platform,
  conversionActions
) => {
  const keys = new Set();
  visitMultipleConversionNode(
    platformStatistics?.campaign,
    platform,
    conversionActions,
    keys
  );

  ["adsGroups", "ads", "assetsGroups"].forEach((bucket) => {
    const map = platformStatistics?.[bucket];
    if (!map || typeof map !== "object") return;
    Object.values(map).forEach((raw) =>
      visitMultipleConversionNode(raw, platform, conversionActions, keys)
    );
  });

  ["keywords", "webpages"].forEach((bucket) => {
    const byGroup = platformStatistics?.[bucket];
    if (!byGroup || typeof byGroup !== "object") return;
    Object.values(byGroup).forEach((inner) => {
      if (!inner || typeof inner !== "object") return;
      Object.values(inner).forEach((raw) =>
        visitMultipleConversionNode(raw, platform, conversionActions, keys)
      );
    });
  });

  return [...keys];
};

export const getCampaignMetricLabel = (metric, platform, t) => {
  if (
    typeof metric === "string" &&
    metric.startsWith(MULTIPLE_CONVERSION_PREFIX)
  ) {
    const groupKey = metric.slice(MULTIPLE_CONVERSION_PREFIX.length);
    if (groupKey.includes("-")) {
      return getTranslatedGroupName(groupKey, t);
    }
    return groupKey;
  }

  const translation = MAP_TRANSLATIONS[metric];
  if (translation) {
    if (typeof translation === "string") {
      return t(translation);
    }
    return translation;
  }

  const metricsToBrokenDownByPlatform =
    METRICS_TO_BROKEN_DOWN_BY_PLATFORM[platform];
  if (!metricsToBrokenDownByPlatform) {
    return metric;
  }

  const actionConfig = metricsToBrokenDownByPlatform.find(
    (config) =>
      config.countMetricName === metric || config.costMetricName === metric
  );
  if (actionConfig) {
    if (metric === actionConfig.countMetricName) {
      return t(actionConfig.countLabelKey);
    }
    if (metric === actionConfig.costMetricName) {
      return t(actionConfig.costLabelKey);
    }
  }

  return metric;
};
